import React from "react";
import { Box, LinearProgress, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { Science as ScienceIcon } from "@mui/icons-material";
import { DataTableCard } from "../../components/organisms";
import {
  ClickableRow,
  DateCell,
  Dash,
  EDGE_L,
  EDGE_R,
  FillerRow,
  PatientCell,
  PlainHead,
  ROWS_PER_PAGE_OPTIONS,
  SkeletonRows,
  SortHead,
  StatusPill,
  TextCell,
  cellSx,
  subText,
} from "../../components/organisms/ClinicRecordList";
import { COLORS } from "../../styles/uiConstants";
import { useClinicTheme } from "../../theme";
import { STATUS_META, TYPE_LABELS, getStatusKey, itemNames } from "./constants";
import useTerms from "../../hooks/useTerms";

const COLS = 5;

/** Sonuç durumu: laboratuvarda girilen sonuç / test sayısı, görüntülemede dosya sayısı. */
function ResultCell({ row, palette }) {
  const files = (row.files || []).length;
  if (row.type === "imaging") {
    return (
      <TableCell sx={cellSx}>
        {files ? <Typography sx={subText}>{files} dosya yüklendi</Typography> : <Dash />}
      </TableCell>
    );
  }
  const items = row.laboratoryItems || [];
  const done = items.filter((i) => i?.isCompleted || String(i?.resultValue || "").trim()).length;
  if (!items.length) {
    return (
      <TableCell sx={cellSx}>
        <Dash />
      </TableCell>
    );
  }
  return (
    <TableCell sx={cellSx}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, pr: 2 }}>
        <LinearProgress
          variant="determinate"
          value={Math.min(100, (done / items.length) * 100)}
          sx={{
            flex: 1,
            maxWidth: 90,
            height: 4,
            borderRadius: 2,
            bgcolor: COLORS.slate100,
            "& .MuiLinearProgress-bar": { bgcolor: palette.primary, borderRadius: 2 },
          }}
        />
        <Typography sx={{ ...subText, whiteSpace: "nowrap" }}>
          {done}/{items.length} sonuç
        </Typography>
      </Box>
    </TableCell>
  );
}

export default function InvestigationsTable({ list, isLoading, empty, onOpenDetail }) {
  const terms = useTerms();
  const { palette } = useClinicTheme();
  const sortProps = { sort: list.sort, dir: list.dir, onSort: list.onSort };

  return (
    <DataTableCard
      loading={Boolean(isLoading)}
      variant="flat"
      empty={!isLoading && list.filtered.length === 0}
      emptyIcon={<ScienceIcon />}
      emptyTitle={empty.title}
      emptyMessage={empty.message}
      page={list.page}
      rowsPerPage={list.rowsPerPage}
      totalCount={list.filtered.length}
      onPageChange={list.onPageChange}
      onRowsPerPageChange={list.onRowsPerPageChange}
      rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
    >
      <Table stickyHeader size="small" sx={{ tableLayout: "fixed", minWidth: 960 }}>
        <TableHead>
          <TableRow>
            <SortHead field="name" label={terms.patient} {...sortProps} sx={{ ...EDGE_L, width: "26%" }} />
            <PlainHead sx={{ width: "32%" }}>Tetkik</PlainHead>
            <PlainHead sx={{ width: "16%" }}>Sonuç</PlainHead>
            <SortHead field="date" label="Tarih" defaultDir="desc" {...sortProps} sx={{ width: "12%" }} />
            <PlainHead sx={{ ...EDGE_R, width: "14%" }}>Durum</PlainHead>
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoading ? (
            <SkeletonRows rows={Math.min(10, list.rowsPerPage)} cols={COLS} />
          ) : list.pagedRows.length ? (
            list.pagedRows.map((row) => {
              const statusKey = getStatusKey(row);
              const names = itemNames(row);
              const typeLabel = TYPE_LABELS[row.type] || "Tetkik";
              const note = String(row.note || "").trim();
              return (
                <ClickableRow key={row._id} onClick={() => onOpenDetail(row)} palette={palette}>
                  <PatientCell patient={row.patient} />
                  <TextCell
                    main={names.length ? names.slice(0, 3).join(", ") : note || typeLabel}
                    sub={[typeLabel, names.length > 3 ? `+${names.length - 3} daha` : null].filter(Boolean).join(" · ")}
                    tooltip={names.length > 3 ? names.join(", ") : note && names.length ? note : null}
                  />
                  <ResultCell row={row} palette={palette} />
                  <DateCell value={row.createdAt} />
                  <TableCell sx={{ ...cellSx, ...EDGE_R }}>
                    <StatusPill label={STATUS_META[statusKey].label} tone={STATUS_META[statusKey].tone} palette={palette} />
                  </TableCell>
                </ClickableRow>
              );
            })
          ) : (
            <FillerRow cols={COLS} />
          )}
        </TableBody>
      </Table>
    </DataTableCard>
  );
}
